<script setup lang="ts">
import GbButton from '@/components/GbButton.vue'
import { CONTENT_TYPES } from '@/util/access_visibility'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import { useLibraryStore } from './library.store'
import { storeToRefs } from 'pinia'

const confirm = useConfirm()
const libraryStore = useLibraryStore()
const { hasRemoveContentPerms } = storeToRefs(libraryStore)

const emit = defineEmits<{
  remove: []
}>()

const props = defineProps({
  resource: {
    type: String,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  collection: {
    type: String,
    required: true
  },
  onRemoveCallback: {
    type: Function,
    required: false,
    default: null
  }
})
const typeToHuman = (typeOfResource: string): string => {
  switch (typeOfResource) {
    case CONTENT_TYPES.ATTACHMENT_LINK:
      return 'Attachment'
    case CONTENT_TYPES.PRESENTLEVEL:
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      return 'Present Levels Statement'
    case CONTENT_TYPES.SAVED_SUPER_PAGE:
    case CONTENT_TYPES.SAVED_GOAL:
      return 'Goal'
    case CONTENT_TYPES.SAVED_STRATEGY:
      return 'Strategy'
    case CONTENT_TYPES.RESOURCE:
      return 'Resource'
    case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
      return 'Impact of Disability Statement'
    default:
      return ''
  }
}

const removeResource = () => {
  confirm.require({
    group: props.resource + '_' + props.collection,
    message: `Are you sure you want to delete this ${typeToHuman(props.type).toLowerCase()} from this folder? This action is irreversible.`,
    header: `Delete ${typeToHuman(props.type)}`,
    accept: async () => {      
      await libraryStore.deleteResource({
        uuid: props.resource,
        type: props.type,
        collection: props.collection
      })
      
      if (props.onRemoveCallback) {
        props.onRemoveCallback()
      }

      emit('remove')
    }
  })
}
</script>

<template>
  <GbButton
    v-if="hasRemoveContentPerms"
    gbTheme="redTertiary"
    aria-label="Remove item from collection"
    :iconOnly="true"
    rounded
    size="small"
    @click="removeResource"
    @keypress="removeResource"
    class="remove-resource-button !absolute right-px top-px z-50 opacity-0 hover:bg-red-100 focus:bg-red-100 focus:text-red-400 focus:opacity-100"
    v-bind="$attrs"
  >
    <span class="material-icons-round">close</span>
  </GbButton>
  <ConfirmDialog :group="`${props.resource}_${props.collection}`" class="w-auto md:w-[38rem]">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col rounded-md bg-surface-50">
        <div class="mt-2 flex justify-between border-b border-gray-300 p-3">
          <span class="block pl-3 text-xl font-medium">{{ message.header }}</span>
          <GbButton
            size="small"
            rounded
            :iconOnly="true"
            aria-label="close dialog"
            gbTheme="grayTertiary"
            class="mr-4 self-start"
            @click="rejectCallback"
          >
            <span class="material-icons-round">close</span>
          </GbButton>
        </div>
        <p class="py-5 pl-7 pr-12">{{ message.message }}</p>
        <div class="flex items-center justify-end gap-2 border-t border-gray-300 p-4">
          <GbButton
            label="Cancel"
            outlined
            @click="rejectCallback"
            gbTheme="grayOutlineSecondary"
          ></GbButton>
          <GbButton label="Delete" @click="acceptCallback" gbTheme="redPrimary"></GbButton>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>